import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "../App.css";
import "../Styles/Dashboard.css";

function Chart() {
  const [chartData, setChartData] = useState([]);
  const [chartData2, setChartData2] = useState([]);
  const [loadingBar, setLoadingBar] = useState(true);
  const [loadingPie, setLoadingPie] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API}/getExamCount`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setChartData(data.data || []);
        } else {
          console.error("Error fetching exam count:", data.message);
        }
      })
      .catch((error) => console.error("Fetch error:", error))
      .finally(() => setLoadingBar(false));
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API}/getResultsCount`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setChartData2(data.data || []);
        } else {
          console.error("Error fetching results count:", data.message);
        }
      })
      .catch((error) => console.error("Fetch error:", error))
      .finally(() => setLoadingPie(false));
  }, []);

  const shortenLabel = (text, max = 18) => {
    const str = String(text || "");
    if (str.length <= max) return str;
    return `${str.substring(0, max)}...`;
  };

  const participationData = useMemo(() => {
    return (chartData || []).map((item) => ({
      fullLabel: item.label,
      label: shortenLabel(item.label, 16),
      y: Number(item.y || 0),
    }));
  }, [chartData]);

  const performanceData = useMemo(() => {
    const total = (chartData2 || []).reduce(
      (sum, item) => sum + Number(item.y || 0),
      0,
    );

    return (chartData2 || []).map((item) => {
      const value = Number(item.y || 0);
      const percentage = total > 0 ? (value / total) * 100 : 0;

      return {
        label: item.label,
        y: value,
        percentage,
      };
    });
  }, [chartData2]);

  const pieColors = [
    "#263F86",
    "#5B4FCC",
    "#A63D00",
    "#B42323",
    "#147D5A",
    "#176B8A",
  ];

  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const item = payload[0]?.payload;
      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: "10px",
            padding: "10px 12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{ fontWeight: 700, color: "#263F86", marginBottom: "4px" }}
          >
            {item?.fullLabel || label}
          </div>
          <div style={{ color: "#555", fontSize: "12px" }}>
            Participants: {payload[0].value}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0]?.payload;
      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: "10px",
            padding: "10px 12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{ fontWeight: 700, color: "#263F86", marginBottom: "4px" }}
          >
            {item?.label}
          </div>
          <div style={{ color: "#555", fontSize: "12px" }}>
            Score: {Number(item?.y || 0).toFixed(2)}
          </div>
          <div style={{ color: "#555", fontSize: "12px" }}>
            Percentage: {Number(item?.percentage || 0).toFixed(2)}%
          </div>
        </div>
      );
    }
    return null;
  };

  const renderLoading = () => (
    <div className="dashboard-chart-state">
      <Spinner animation="border" style={{ color: "#263F86" }} />
      <p>
        Loading chart...
      </p>
    </div>
  );

  const renderEmpty = () => (
    <div className="dashboard-chart-state dashboard-chart-state--empty">
      <strong>No data available</strong>
      <span>Chart data will appear after assessments are completed.</span>
    </div>
  );

  return (
    <div className="dashboard-chart-grid">
      <article className="dashboard-chart-card" aria-labelledby="participation-chart-title">
        <div className="dashboard-chart-heading">
          <h3 id="participation-chart-title">Assessment participation</h3>
          <p>Participants recorded for each assessment</p>
        </div>

            {loadingBar ? (
              renderLoading()
            ) : participationData.length > 0 ? (
              <div className="dashboard-chart-canvas">
                <ResponsiveContainer>
                  <BarChart
                    data={participationData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis
                      dataKey="label"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      tick={{ fontSize: 11, fill: "#64748B" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomBarTooltip />} />

                    <Bar
                      dataKey="y"
                      name="Participants"
                      fill="#263F86"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              renderEmpty()
            )}
      </article>

      <article className="dashboard-chart-card" aria-labelledby="performance-chart-title">
        <div className="dashboard-chart-heading">
          <h3 id="performance-chart-title">Top user performance</h3>
          <p>Score distribution across the top five users</p>
        </div>

            {loadingPie ? (
              renderLoading()
            ) : performanceData.length > 0 ? (
              <div className="dashboard-chart-canvas">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={performanceData}
                      dataKey="y"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={96}
                      innerRadius={58}
                      paddingAngle={2}
                    >
                      {performanceData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={pieColors[index % pieColors.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: "12px" }}
                      formatter={(value) => (
                        <span style={{ fontSize: "12px" }}>
                          {shortenLabel(value, 18)}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              renderEmpty()
            )}
      </article>
    </div>
  );
}

export default Chart;
